import type { SyntheticColumn } from "./analytics";
import type {
  EventCondition,
  PropertyCondition,
  RootCondition,
} from "./condition";
import type { JourneyEventColumn } from "./journey-event";
import type {
  FormulaTraitConfig,
  TraitCondition,
  TraitConfig,
  TraitType,
} from "./trait-definitions";

export enum ColumnType {
  String = "string",
  Number = "number",
  BigInt = "bigint",
  Boolean = "boolean",
  Timestamp = "timestamp",
  Date = "date",
  Json = "json",
  JsonArrayStrings = "json-array-strings",
  JsonArrayNumbers = "json-array-numbers",
  JsonArrayObjects = "json-array-objects",
  Null = "null",
  Unknown = "unknown",
}

export function isColumnType(value: string): value is ColumnType {
  return Object.values(ColumnType).includes(value as ColumnType);
}

export type Column =
  | RawColumn
  | RelatedColumn
  | TransformedColumn
  | StaticValueColumn;

export type RelatedColumn = {
  type: "related";
  path: string[];
  column:
    | RawColumn
    | TraitColumn
    | EventTraitColumn
    | InlineAggregatedTrait
    | JourneyEventColumn;
};

export type RawColumn = {
  type: "raw";
  modelId: string;
  name: string;
};

export type TransformedColumn = {
  type: "transformed";
  column: TraitColumn | InlineFormulaTrait | ConditionTrait | ConditionCase;
};

export type JsonPathColumn = {
  type: "json_path";
  castType: ColumnType;
  column: RawColumn | RelatedColumn;
  path: string;
};

// XXX: This trait is only used from internally generated queries (namely,
// Decision Engine message filters) and isn't directly exposed to customers.
export type ConditionTrait = {
  type: "condition_trait";
  condition: RootCondition;
};

// Like `ConditionTrait`, this is only produced by internally generated queries
// (dynamic-content slots) and isn't exposed in the audience builder. It
// collapses an ordered set of conditions into a single value column: each
// case's condition compiles to a predicate, and the column resolves to the
// first matching case's `value` (an ordered CASE — first match wins — so it
// mirrors if/elsif priority). `fallback` is the ELSE value; null → NULL.
export type ConditionCase = {
  type: "condition_case";
  cases: { condition: RootCondition; value: string }[];
  fallback: string | null;
};

export type StaticValueColumn = {
  type: "static_value";
  value: string | number;
  valueType: "string" | "number";
};

export type UnbucketedGroupBy = {
  column: RawColumn | RelatedColumn | SyntheticColumn | TransformedColumn;
};

export type BucketedGroupBy = UnbucketedGroupBy & {
  buckets: {
    start: number;
    end?: number;
  }[];
};

export type GroupBy = UnbucketedGroupBy | BucketedGroupBy;

export type TraitColumn = {
  type: "trait";
  traitDefinitionId: string;
  conditions:
    | PropertyCondition[] // Legacy format that is still supported by the backend
    | TraitCondition[]; // The frontend will convert it to this type and send it to the backend

  // Only used for syncing
  // 1) Mapper component uses the name as a label in the UI
  // 2) Visual column resolver uses this to compute a unique, friendly name
  name?: string;
};

// This is essentially a `TraitColumn` that hasn't been materialized in the DB so it doesn't have a `traitDefinitionId`
export type InlineTraitColumn = InlineAggregatedTrait | InlineFormulaTrait;

export type InlineAggregatedTrait = {
  type: "inline_trait";
  traitType: Exclude<TraitType, TraitType.Formula>;
  traitConfig: Exclude<TraitConfig, FormulaTraitConfig>;
  conditions: TraitCondition[];
  relationshipId: string;
  /**
   * When true, catalog recommendation traits are evaluated as an ordered list of
   * recommended item labels (one row per user) instead of MIN(rank). Only set by
   * the trait preview UI; audience and sync evaluation leave this unset so the
   * scalar MIN(rank) aggregation is used.
   */
  preview?: boolean;
};

export type InlineFormulaTrait = {
  type: "inline_trait";
  traitType: TraitType.Formula;
  traitConfig: FormulaTraitConfig;
};

export type EventTraitColumn = {
  type: "event_trait";
  filteredEvent: Omit<EventCondition, "operator" | "value">;
  traitType: TraitType;
  traitConfig: TraitConfig;
};

export interface MergedColumn extends RelatedColumn {
  column: RawColumn;
}

export type JourneyMemberStateColumn = {
  type: "journey_member_state";
  field: "entered_at";
};

export type ColumnReference =
  | JourneyMemberStateColumn
  | RawColumn
  | RelatedColumn
  | SyntheticColumn
  | TransformedColumn
  | TraitColumn
  | InlineTraitColumn
  | ConditionTrait
  | ConditionCase
  | EventTraitColumn
  | SyntheticColumn
  | JsonPathColumn
  | JourneyEventColumn
  | StaticValueColumn;

export const isColumnReference = (
  property: unknown,
): property is ColumnReference => {
  return property != null && typeof property === "object";
};

export const isJourneyMemberStateColumn = (
  property: unknown,
): property is JourneyMemberStateColumn =>
  isColumnReference(property) && property.type === "journey_member_state";

export const isRawColumn = (
  property: string | ColumnReference | null,
): property is RawColumn => {
  return isColumnReference(property) && property?.type === "raw";
};

export const isRelatedColumn = (
  property: string | ColumnReference | null,
): property is RelatedColumn => {
  return isColumnReference(property) && property?.type === "related";
};

export const isTransformedColumn = (
  property: string | ColumnReference | null,
): property is TransformedColumn => {
  return isColumnReference(property) && property?.type === "transformed";
};

export const isTraitColumn = (
  column: ColumnReference | null,
): column is TraitColumn => column?.type === "trait";

export const isEventTraitColumn = (
  column: ColumnReference | null,
): column is EventTraitColumn =>
  isColumnReference(column) && column?.type === "event_trait";

export const isMergedColumn = (
  column: ColumnReference | string,
): column is MergedColumn =>
  isRelatedColumn(column) &&
  !isTraitColumn(column.column) &&
  !isInlineTraitColumn(column.column) &&
  !isJourneyEventColumn(column.column);

export const isInlineTraitColumn = (
  column: ColumnReference | string | null,
): column is InlineTraitColumn => {
  return (
    Boolean(column) &&
    typeof column === "object" &&
    column?.type === "inline_trait"
  );
};

export function isRelatedJourneyEventColumn(
  property: string | null | undefined | ColumnReference,
): property is RelatedColumn & { column: JourneyEventColumn } {
  return (
    Boolean(property) &&
    typeof property === "object" &&
    isRelatedColumn(property) &&
    property?.type === "related" &&
    property?.column?.type === "journey_event"
  );
}

export function isJourneyEventColumn(
  property: string | null | undefined | ColumnReference,
): property is JourneyEventColumn {
  return (
    Boolean(property) &&
    typeof property === "object" &&
    property?.type === "journey_event"
  );
}

export function isJsonPathColumn(
  property: string | ColumnReference | null,
): property is JsonPathColumn {
  return (
    !!property && isColumnReference(property) && property?.type === "json_path"
  );
}

export const isStaticValueColumn = (
  column: ColumnReference | null | undefined,
): column is StaticValueColumn =>
  column != null && column.type === "static_value";
